// =============================================================================
// features/sales/campaigns/wizard/steps/Step2Audience.tsx — Etapa 2: escolhe
// a FONTE do público (F35-S08, doc 26 §6.6). Não classifica nada aqui — a
// higienização acontece na etapa 3, chamando a API (ver aviso em
// hooks/sales/useCampaignWizard.ts).
//
// Três fontes, três affordances diferentes por design:
//   'filter' — critérios do CRM, com prévia/aplicação em duas chamadas
//              não-destrutivas (POST /audience/preview → /audience/apply).
//   'manual' — ids colados, mesmo contrato de prévia/aplicação.
//   'import' — referência a um lote já concluído; a API NÃO tem uma prévia
//              não-destrutiva para esta fonte (só POST /members/import, que
//              já materializa) — avisado explicitamente na etapa 3, nunca
//              escondido do gestor.
// =============================================================================

import * as React from 'react';

import { Input } from '../../../../../components/ui/Input';
import { cn } from '../../../../../lib/cn';
import { FormTextarea } from '../../../components/FormTextarea';
import { AudienceFilterFields } from '../components/AudienceFilterFields';
import { WizardStepShell } from '../components/WizardStepShell';
import type { AudienceSourceKind, AudienceState } from '../types';

const SOURCE_TABS: { value: AudienceSourceKind; label: string }[] = [
  { value: 'filter', label: 'Filtro do CRM' },
  { value: 'manual', label: 'Seleção manual' },
  { value: 'import', label: 'Lote de importação' },
];

export interface Step2AudienceProps {
  values: AudienceState;
  onChange: (values: AudienceState) => void;
  onContinue: () => void;
  onBack: () => void;
}

export function Step2Audience({
  values,
  onChange,
  onContinue,
  onBack,
}: Step2AudienceProps): React.JSX.Element {
  return (
    <WizardStepShell
      title="Público da campanha"
      description="De onde vêm os clientes desta campanha — a higienização (próxima etapa) mostra exatamente quem entra e quem fica de fora, e por quê."
      onBack={onBack}
      onContinue={onContinue}
    >
      <div role="tablist" aria-label="Fonte do público" className="flex flex-wrap gap-2">
        {SOURCE_TABS.map((tab) => {
          const active = values.kind === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange({ ...values, kind: tab.value })}
              className={cn(
                'px-4 py-2 rounded-sm font-sans text-sm font-semibold transition-colors duration-fast',
                'border min-h-[40px]',
                'outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
                active
                  ? 'bg-azul text-white border-azul'
                  : 'bg-surface-1 text-ink-2 border-border-strong hover:border-azul hover:text-azul',
              )}
              style={active ? { boxShadow: 'var(--elev-1)' } : {}}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {values.kind === 'filter' && (
        <AudienceFilterFields
          value={values.filter}
          onChange={(filter) => onChange({ ...values, filter })}
        />
      )}

      {values.kind === 'manual' && (
        <FormTextarea
          id="wizard-audience-manual"
          label="Ids dos clientes"
          hint="Um id por linha (ou separados por vírgula) — até 5.000 por campanha."
          rows={8}
          placeholder={'123e4567-e89b-12d3-a456-426614174000\n223e4567-e89b-12d3-a456-426614174001'}
          value={values.manualLeadIdsRaw}
          onChange={(e) => onChange({ ...values, manualLeadIdsRaw: e.target.value })}
        />
      )}

      {values.kind === 'import' && (
        <div className="flex flex-col gap-3">
          <Input
            id="wizard-audience-import-batch"
            label="Id do lote de importação"
            hint="Gerado ao concluir uma importação em Leads → Importar. Copie o id da tela final."
            placeholder="123e4567-e89b-12d3-a456-426614174000"
            value={values.importBatchId}
            onChange={(e) => onChange({ ...values, importBatchId: e.target.value })}
          />
          <p className="font-sans text-xs text-ink-3 leading-relaxed">
            Ainda não importou a planilha? Abra a importação de leads em outra aba, conclua o
            processo e volte aqui com o id do lote — o rascunho desta campanha fica salvo.
          </p>
        </div>
      )}
    </WizardStepShell>
  );
}
